import { nodeConfig } from '@scripts/mixins';
import {
	horizontalKeyNavigationHandler,
	verticalKeyNavigationHandler
} from '@scripts/helpers/key-navigation';
import deeplink, { updateHash, updateQueryParam } from '@scripts/helpers/deeplink';
import * as CONSTANTS from '@scripts/helpers/constants';

const NAMESPACE = 'tabs';
const DEFAULT_SELECTOR = '[data-js-tabs]';
const TAB_SELECTOR = '[data-js-tabs-tab]';
const PANEL_SELECTOR = '[data-js-tabs-panel]';
const LIST_SELECTOR = '[data-js-tabs-list]';

/**
 * @typedef {Object} TabsSettings
 * @property {boolean} [automatic=true] - Selection follows focus during arrow navigation.
 * @property {'horizontal'|'vertical'} [orientation='horizontal'] - Tablist orientation.
 * @property {boolean|string} [deeplink=false] - Sync active tab with URL hash.
 * @property {boolean} [hiddenUntilFound=true] - Use hidden="until-found" for browser find-in-page.
 */
const DEFAULT_SETTINGS = {
	automatic: true,
	orientation: 'horizontal',
	deeplink: false,
	hiddenUntilFound: true
};

/**
 * Accessible tabs pattern.
 * @fires tabs:change
 */
class Tabs {
	/**
	 * @param {HTMLElement} node
	 * @param {TabsSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;

		this.elList = this.elNode.querySelector(LIST_SELECTOR);
		this.elTabs = Array.from(this.elNode.querySelectorAll(TAB_SELECTOR));
		this.elPanels = Array.from(this.elNode.querySelectorAll(PANEL_SELECTOR));
	}

	init() {
		if (!this.elList || !this.elTabs.length || !this.elPanels.length) return;

		this._setup();
		this._bindUI();

		if (this.config.deeplink) {
			this._setupDeepLinks();
		}
	}

	_setup() {
		if (this.config.orientation === 'vertical') {
			this.elList.setAttribute('aria-orientation', 'vertical');
		}

		let initialIndex = 0;
		let deeplinkIndex = -1;
		let domIndex = -1;

		this.elTabs.forEach((tab, index) => {
			const panel = this.elPanels[index];

			if (!panel) return;

			if (this.config.deeplink && typeof this.config.deeplink === 'string') {
				const paramValue = (new URLSearchParams(window.location.search)).get(this.config.deeplink);
				if (paramValue && panel.id === paramValue) {
					deeplinkIndex = index;
				}
			} else if (this.config.deeplink && window.location.hash === `#${panel.id}`) {
				deeplinkIndex = index;
			}

			if (tab.getAttribute('aria-selected') === 'true') {
				domIndex = index;
			}
		});

		if (deeplinkIndex > -1) {
			initialIndex = deeplinkIndex;
		} else if (domIndex > -1) {
			initialIndex = domIndex;
		}

		this._activate(initialIndex, false, false, false);
	}

	_setupDeepLinks() {
		this.elPanels.forEach((panel, index) => {
			if (!panel.id) return;

			if (typeof this.config.deeplink === 'string') {
				deeplink.registerQueryParam(this.config.deeplink, panel.id, () => {
					this._activate(index);
				});
			} else {
				deeplink.register(`${PANEL_SELECTOR}#${panel.id}`, () => {
					this._activate(index);
				});
			}
		});
	}

	/**
	 * @param {number} index
	 * @param {boolean} [setFocus=true]
	 * @param {boolean} [updateUrlState=true]
	 * @param {boolean} [dispatchEvent=true]
	 */
	_activate(index, setFocus = true, updateUrlState = true, dispatchEvent = true) {
		if (index < 0 || index >= this.elTabs.length) return;

		this.elTabs.forEach((tab, i) => {
			const isActive = i === index;
			const panel = this.elPanels[i];

			tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
			tab.setAttribute('tabindex', isActive ? '0' : '-1');
			tab.classList.toggle(CONSTANTS.ACTIVE_CLASS, isActive);

			if (!panel) return;

			panel.classList.toggle(CONSTANTS.OPEN_CLASS, isActive);

			if (isActive) {
				panel.removeAttribute('hidden');

				if (!panel.querySelector(CONSTANTS.FOCUSABLE_ELEMENTS_SELECTOR)) {
					panel.setAttribute('tabindex', '0');
				}
			} else {
				panel.setAttribute('hidden', this.config.hiddenUntilFound ? 'until-found' : '');
				panel.removeAttribute('tabindex');
			}
		});

		if (setFocus) {
			this.elTabs[index].focus();
		}

		if (updateUrlState && this.config.deeplink && this.elPanels[index]?.id) {
			if (typeof this.config.deeplink === 'string') {
				updateQueryParam(this.config.deeplink, this.elPanels[index].id, true);
			} else {
				updateHash(this.elPanels[index].id, true);
			}
		}

		if (dispatchEvent) {
			this._dispatch('change', { index });
		}
	}

	/** @param {string} action */
	_dispatch(action, detail = {}) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${NAMESPACE}:${action}`, {
				bubbles: true,
				detail: { instance: this, ...detail }
			})
		);
	}

	_bindUI() {
		this._onTabClick = this._onTabClick.bind(this);
		this._onKeydown = this._onKeydown.bind(this);
		this._onBeforeMatch = this._onBeforeMatch.bind(this);

		this.elTabs.forEach((tab) => {
			tab.addEventListener('click', this._onTabClick);
			tab.addEventListener('keydown', this._onKeydown);
		});

		if (this.config.hiddenUntilFound) {
			this.elPanels.forEach((panel) => {
				panel.addEventListener('beforematch', this._onBeforeMatch);
			});
		}
	}

	_onTabClick(event) {
		this._activate(this.elTabs.indexOf(event.currentTarget));
	}

	_onBeforeMatch(event) {
		const index = this.elPanels.indexOf(event.target);

		if (index === -1) return;

		this._activate(index, false);
	}

	/** Defensive Space handler for non-button tab elements. */
	_onKeydown(event) {
		if (event.key === ' ') {
			event.preventDefault();
			this._activate(this.elTabs.indexOf(event.currentTarget));

			return;
		}

		const handlerConfig = {
			event,
			items: this.elTabs,
			getTrigger: (el) => el,
			loop: true
		};

		const handler =
			this.config.orientation === 'vertical'
				? verticalKeyNavigationHandler
				: horizontalKeyNavigationHandler;

		const didMoveFocus = handler(handlerConfig);

		if (didMoveFocus && this.config.automatic) {
			const index = this.elTabs.indexOf(document.activeElement);

			if (index !== -1) {
				this._activate(index, false);
			}
		}
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Tabs(node, config);

		module.init();

		return module;
	});
}
